import React from "react";

const r2 = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const n3 = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

// Eligible commission lines: sheet (sqft) lines only — hardware / service never earn commission.
export function commissionLines(totals, materialItems) {
  const byId = Object.fromEntries(materialItems.map((m) => [m.id, m]));
  return totals.lines.filter((l) => l.sqft > 0).map((l) => {
    const m = byId[l.item_id] || {};
    const isHw = String(m.category || "").toLowerCase() === "hardware";
    if (!l.item_id || isHw || m.item_type === "SERVICE" || m.is_service) return null;
    const eff = l.net / l.sqft;
    const cp = Number(m.company_price || 0);
    const auto = cp > 0 ? Math.max(0, Math.round((eff - cp) * 100) / 100) : null;
    return { ...l, name: m.name || l.item_name, eff, auto, floor: Number(m.welder_rate_sqft || 0) };
  }).filter(Boolean);
}

export function staffTotal(lines, rates) {
  return lines.reduce((t, l) => t + (l.auto == null ? 0 : (rates[l.idx] ?? l.auto) * l.sqft), 0);
}

export function welderTotal(lines, rates) {
  return lines.reduce((t, l) => t + (Number(rates[l.idx]) || 0) * l.sqft, 0);
}

export const welderErrors = (lines, rates) =>
  lines.filter((l) => l.floor > 0 && l.eff - (Number(rates[l.idx]) || 0) < l.floor - 0.005);

// kind: "staff" | "welder". Staff rates prefilled with auto; welder starts empty.
export default function CommissionLines({ kind, lines, rates, setRate, editable, pending }) {
  const total = kind === "staff" ? staffTotal(lines, rates) : welderTotal(lines, rates);
  const shown = kind === "staff" ? lines.filter((l) => l.auto != null) : lines;
  if (!shown.length) {
    return <div className="text-[11px] text-slate-500" data-testid={`${kind}-comm-no-lines`}>Koi sheet line nahi (hardware / service pe commission nahi).</div>;
  }
  return (
    <div className="space-y-1.5" data-testid={`${kind}-comm-lines`}>
      {shown.map((l) => {
        const v = kind === "staff" ? (rates[l.idx] ?? l.auto) : (rates[l.idx] ?? "");
        const bad = kind === "welder" && l.floor > 0 && l.eff - (Number(v) || 0) < l.floor - 0.005;
        return (
          <div key={l.idx} className="grid grid-cols-[minmax(0,1.4fr)_auto_auto_minmax(0,0.9fr)] gap-2 items-center text-xs" data-testid={`${kind}-comm-line-${l.idx}`}>
            <div className="min-w-0">
              <div className="text-slate-200 truncate">{l.name}</div>
              <div className="text-[10px] font-mono-num text-slate-500">
                {n3(l.sqft)} sqft · rate {r2(l.eff)}
                {kind === "staff" ? ` · auto ${r2(l.auto)}/sqft` : l.floor > 0 ? ` · max ${r2(Math.max(l.eff - l.floor, 0))}/sqft` : ""}
              </div>
            </div>
            <span className="text-[10px] font-mono-num text-slate-500">₹/SQFT</span>
            <input type="number" min="0" step="0.01" value={v} disabled={!editable}
              onChange={(e) => setRate(l.idx, e.target.value === "" ? undefined : Number(e.target.value))}
              placeholder={kind === "welder" ? "type" : ""}
              className={"h-8 w-20 bg-slate-800/60 border rounded-sm px-2 font-mono-num text-amber-200 disabled:opacity-60 " + (bad ? "border-red-500" : "border-amber-500/40")}
              data-testid={`${kind}-comm-rate-${l.idx}`} />
            <div className="text-right font-mono-num text-amber-200" data-testid={`${kind}-comm-amount-${l.idx}`}>{r2((Number(v) || 0) * l.sqft)}</div>
            {bad && <div className="col-span-4 text-[11px] text-red-400 font-bold" data-testid={`${kind}-comm-error-${l.idx}`}>Below welder rate ₹{l.floor}</div>}
          </div>
        );
      })}
      <div className="flex justify-between border-t border-amber-500/20 pt-1.5 text-sm">
        <span className="text-slate-400">{kind === "staff" ? "Staff" : "Welder"} commission total{pending ? " · PENDING APPROVAL" : ""}</span>
        <b className="font-mono-num text-amber-300" data-testid={`${kind}-comm-total`}>{r2(total)}</b>
      </div>
      {pending && <div className="text-[11px] text-amber-400" data-testid="staff-comm-pending-note">Main Admin ki permission ke baad hi ye commission count hoga.</div>}
    </div>
  );
}
